// src/admin/pages/tabs/StorageManagementTab.js
import React, { useState, useEffect, useCallback } from 'react';
import { HardDrive, FileText, Calendar, Database, RefreshCw } from 'lucide-react';
import { supabase } from '../../../config';

const StorageManagementTab = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalStorage, setTotalStorage] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);

  const loadStorageData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load files from database (primary source)
      await loadFilesFromDatabase();
    } catch (error) {
      console.error('Error loading storage data:', error);
      setFiles([]);
      setTotalStorage(0);
      setTotalFiles(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStorageData();
  }, [loadStorageData]);

  const loadFilesFromDatabase = async () => {
    try {
      // Load files from database with project info
      const { data: dbFiles, error: dbError } = await supabase
        .from('dataroom_items')
        .select(`
          id,
          name,
          file_size,
          file_path,
          file_url,
          created_at,
          project_id,
          project:dataroom_projects!dataroom_items_project_id_fkey(
            id,
            project_name
          )
        `)
        .eq('is_folder', false)
        .order('created_at', { ascending: false });

      if (dbError) throw dbError;

      const formattedFiles = (dbFiles || []).map(file => ({
        name: file.name,
        size: parseInt(file.file_size) || 0,
        file_size: parseInt(file.file_size) || 0,
        created_at: file.created_at,
        storage_path: file.file_path || file.file_url || '',
        project_id: file.project_id,
        project_name: file.project?.project_name || 'Unknown Project',
        id: file.id
      }));

      const total = formattedFiles.reduce((sum, file) => sum + (file.file_size || 0), 0);
      
      setFiles(formattedFiles);
      setTotalStorage(total);
      setTotalFiles(formattedFiles.length);
      
      return formattedFiles;
    } catch (error) {
      console.error('Error loading files from database:', error);
      setFiles([]);
      setTotalStorage(0);
      setTotalFiles(0);
      return [];
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileName = (file) => {
    if (file.name) {
      const parts = file.name.split('/');
      return parts[parts.length - 1] || file.name;
    }
    return 'Unknown File';
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading storage data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Storage Management</h2>
          <p className="text-gray-600 mt-1">View all files in dataroom-files storage</p>
        </div>
        <button
          onClick={loadStorageData}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <FileText className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Files</p>
              <p className="text-2xl font-bold text-gray-900">{totalFiles.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <HardDrive className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Storage</p>
              <p className="text-2xl font-bold text-gray-900">{formatFileSize(totalStorage)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <Database className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Average File Size</p>
              <p className="text-2xl font-bold text-gray-900">
                {totalFiles > 0 ? formatFileSize(totalStorage / totalFiles) : '0 Bytes'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Files Table */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  File Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Project
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  File Size
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Storage Path
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Array.isArray(files) && files.map((file, index) => (
                <tr key={file.id || index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                          <FileText className="h-5 w-5 text-blue-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {getFileName(file)}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {file.project_name || 'Unknown Project'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <HardDrive className="h-4 w-4 text-gray-400 mr-1" />
                      {formatFileSize(file.file_size || file.size || 0)}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <div className="max-w-xs truncate" title={file.storage_path || file.name}>
                      {file.storage_path || file.name || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                      {file.created_at 
                        ? new Date(file.created_at).toLocaleDateString()
                        : 'N/A'}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {files.length === 0 && (
          <div className="text-center py-12">
            <HardDrive className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No files found</h3>
            <p className="mt-1 text-sm text-gray-500">No files have been uploaded to storage yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StorageManagementTab;

